import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, Globe2, ShieldCheck, Clock, User2 } from "lucide-react";
import { LANG_NAMES, tr } from "../lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";

export default function TopNav({ lang, setLang, search, setSearch }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const onAdd = () => {
    if (!user) {
      navigate("/register");
      return;
    }
    if (!user.is_verified) {
      navigate("/verify");
      return;
    }
    navigate("/add-event");
  };

  return (
    <header
      data-testid="top-nav"
      className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/85 border-b border-slate-200"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
        <Link to="/" data-testid="nav-logo" className="font-heading font-extrabold text-2xl tracking-tight text-slate-900 flex items-center gap-0.5">
          ConnectYou<span className="text-sky-500">.</span>
        </Link>

        <div className="hidden sm:flex flex-1 max-w-md mx-4 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="nav-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr(lang, "nav.search_placeholder")}
            className="w-full pl-11 pr-4 py-2.5 rounded-full bg-slate-100 border border-transparent focus:bg-white focus:border-sky-300 focus:ring-4 focus:ring-sky-100 focus:outline-none text-sm font-medium transition-all"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button
            data-testid="nav-add-event-btn"
            onClick={onAdd}
            className="hidden md:inline-flex h-10 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>{tr(lang, "nav.add")}</span>
          </Button>
          <button
            data-testid="nav-add-event-btn-mobile"
            onClick={onAdd}
            className="md:hidden w-10 h-10 rounded-full bg-slate-900 text-white grid place-items-center active:scale-95"
            aria-label={tr(lang, "nav.add")}
          >
            <Plus className="w-4 h-4" />
          </button>

          {/* Language */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-language-switcher"
                className="h-10 px-3 rounded-full bg-white border border-slate-200 hover:border-sky-300 text-sm font-bold flex items-center gap-1.5 transition-colors"
              >
                <Globe2 className="w-4 h-4 text-slate-500" />
                {LANG_NAMES[lang]}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              {Object.keys(LANG_NAMES).map((k) => (
                <DropdownMenuItem
                  key={k}
                  data-testid={`nav-language-${k}`}
                  onSelect={() => setLang(k)}
                  className="font-semibold cursor-pointer"
                >
                  {LANG_NAMES[k]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Auth */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-menu"
                  className="h-10 pl-1 pr-3 rounded-full bg-white border border-slate-200 hover:border-sky-300 text-sm font-bold flex items-center gap-2 transition-colors"
                >
                  <span className="w-8 h-8 rounded-full bg-sky-100 text-sky-700 grid place-items-center font-extrabold text-xs">
                    {user.name?.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase() || <User2 className="w-4 h-4" />}
                  </span>
                  <span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
                  {user.is_verified ? (
                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Clock className="w-4 h-4 text-amber-500" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl w-52">
                <DropdownMenuLabel className="font-bold text-slate-900">{user.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate("/profile")} data-testid="nav-menu-profile" className="font-semibold cursor-pointer">Profil</DropdownMenuItem>
                {!user.is_verified && (
                  <DropdownMenuItem onSelect={() => navigate("/verify")} data-testid="nav-menu-verify" className="font-semibold cursor-pointer">Ověřit občanku</DropdownMenuItem>
                )}
                {user.is_admin && (
                  <DropdownMenuItem onSelect={() => navigate("/admin")} data-testid="nav-menu-admin" className="font-semibold cursor-pointer">Admin panel</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={logout} data-testid="nav-menu-logout" className="font-semibold cursor-pointer text-red-600">Odhlásit</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link
                data-testid="nav-login-btn"
                to="/login"
                className="hidden sm:inline-flex h-10 px-4 items-center rounded-full text-sm font-bold text-slate-700 hover:text-sky-600 transition-colors"
              >
                {tr(lang, "nav.login")}
              </Link>
              <Link
                data-testid="nav-register-btn"
                to="/register"
                className="h-10 px-4 inline-flex items-center rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold transition-colors active:scale-95"
              >
                Registrace
              </Link>
            </>
          )}
        </div>
      </div>

      <div className="sm:hidden px-4 pb-3">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="nav-search-input-mobile"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr(lang, "nav.search_placeholder")}
            className="w-full pl-11 pr-4 py-2.5 rounded-full bg-slate-100 focus:bg-white focus:ring-4 focus:ring-sky-100 focus:outline-none text-sm font-medium border border-transparent focus:border-sky-300"
          />
        </div>
      </div>
    </header>
  );
}
